/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { CloudAccountsSortFields, SortOrder } from "~/generated/graphql";
import { TableSearchInput } from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { AccountQuery } from "../types/AccountQuery";
import { Actions } from "./Actions";
import { CloudAccountsFiltersSidebar } from "./CloudAccountsFiltersSidebar";

const sortOptions = [
  {
    value: CloudAccountsSortFields.Name,
    label: "Account Name",
  },
  {
    value: CloudAccountsSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: CloudAccountsSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: CloudAccountsSortFields.Status,
    label: "Status",
  },
  {
    value: CloudAccountsSortFields.Provider,
    label: "Cloud Provider",
  },
];

type Props = {
  cloudAccountsIds: string[];
  resultsCount?: number;
};

export function CloudAccountTableHeader({
  cloudAccountsIds,
  resultsCount = 0,
}: Props) {
  const { queryState, updateQueryState } = useQueryState<AccountQuery>();

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: CloudAccountsSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (phrase: string) => {
    updateQueryState({
      ...queryState,
      searchPhrase: phrase,
    });
  };

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <FlexContainer justifyContent="space-between">
        <SortSelector<CloudAccountsSortFields>
          options={sortOptions}
          value={queryState.field ?? CloudAccountsSortFields.Name}
          direction={queryState.order ?? SortOrder.Asc}
          onSelect={handleSortField}
          onSortChange={handleSortDirection}
        />
        <Spacer width={theme.spacing.spacing04} />
        <TableSearchInput<AccountQuery>
          onSubmit={handlePhraseField}
          placeholder={`Type a Keyword (e.g. Account Name, Account ID, etc.)`}
        />
        <Spacer width={theme.spacing.spacing04} />
        <CloudAccountsFiltersSidebar resultsCount={resultsCount} />
        <Actions size="large" {...{ cloudAccountsIds }} />
      </FlexContainer>
    </Box>
  );
}
